"use client";

import React from "react";
import { Activity, ArrowRight, Fingerprint, FlaskConical, Pill } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SolutionHub {
  id: string;
  title: string;
  description: string;
  href: string;
  iconType: "chromatography" | "pat" | "rmid" | "tablet";
}
const hubs: SolutionHub[] = [{
  id: "hub-1",
  title: "Chromatography",
  description: "Solvent recovery systems and analytical column workflows to improve lab efficiency and reduce solvent waste.",
  href: "/ChromatographyPage",
  iconType: "chromatography"
}, {
  id: "hub-2",
  title: "Process Analytical Technology",
  description: "Real-time NIR and Raman process analyzers engineered for in-line monitoring and critical quality attribute control.",
  href: "/ProcessAnalyticalTechnologyPage",
  iconType: "pat"
}, {
  id: "hub-3",
  title: "Raw Material Identification",
  description: "Handheld spectroscopic verification systems delivering non-destructive identification through transparent packaging.",
  href: "/RawMaterialIdentificationPage",
  iconType: "rmid"
}, {
  id: "hub-4",
  title: "Tablet Testing",
  description: "Pharmacopeia-compliant physical testing instruments measuring hardness, friability, disintegration, and dissolution.",
  href: "/TabletTestingPage",
  iconType: "tablet"
}];
export default function RelatedHubs() {
  const shouldReduceMotion = useReducedMotion();
  const getHubIcon = (iconType: SolutionHub["iconType"]) => {
    switch (iconType) {
      case "chromatography":
        return <FlaskConical className="size-6 text-primary" aria-hidden="true" />;
      case "pat":
        return <Activity className="size-6 text-primary" aria-hidden="true" />;
      case "rmid":
        return <Fingerprint className="size-6 text-primary" aria-hidden="true" />;
      case "tablet":
        return <Pill className="size-6 text-primary" aria-hidden="true" />;
      default:
        return <FlaskConical className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="relatedsolutionhubs" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-wider uppercase text-primary mb-2 block">
              Integrated Capabilities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
              Related Solution Hubs
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              Explore our core analytical technology domains, compliance-ready instrumentation, and lifecycle support across regulated environments.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/SolutionsPage" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
              View all solutions
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </div>

        {/* 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {hubs.map((hub, index) => <motion.div key={hub.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : index * 0.1,
          ease: "easeOut"
        }} className="h-full">
              <Card className="h-full flex flex-col justify-between border-border/80 bg-card hover:border-primary/50 hover:shadow-md transition-all duration-200 group rounded-xl p-6 gap-0">
                <div>
                  <CardHeader className="p-0 mb-5">
                    <div className="size-12 rounded-lg bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      {getHubIcon(hub.iconType)}
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <CardTitle className="text-xl font-semibold tracking-tight text-foreground group-hover:text-primary transition-colors">
                      {hub.title}
                    </CardTitle>
                    <CardDescription className="mt-2.5 text-sm text-muted-foreground leading-relaxed">
                      {hub.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="p-0 pt-6 mt-6 border-t border-border/60">
                  <Link to={hub.href} className="inline-flex items-center gap-2 text-sm font-semibold text-foreground group-hover:text-primary transition-colors" aria-label={`Explore ${hub.title} solutions`}>
                    <span>Explore Solution</span>
                    <ArrowRight className="size-4 transform transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}